import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const CASH_SETTLING_CODES = new Set(['PAYMENT', 'RECEIPT']);
const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'Cheque'];

const emptyCashLine = () => ({ account_id: '', description: '', gross_amount: '', tax_deducted: '' });
const emptyExplicitLine = () => ({ account_id: '', description: '', debit: '', credit: '', cost_center_id: '', fund_id: '' });

const VoucherCreate = () => {
  const navigate = useNavigate();
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [funds, setFunds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [voucherTypeId, setVoucherTypeId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [fundId, setFundId] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [installmentId, setInstallmentId] = useState('');
  const [utilization, setUtilization] = useState(null);
  const [vehicleId, setVehicleId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [vendorBillId, setVendorBillId] = useState('');
  const [vendorBills, setVendorBills] = useState([]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [purpose, setPurpose] = useState('');
  const [accountingDate, setAccountingDate] = useState('');
  const [lines, setLines] = useState([]);

  const selectedType = voucherTypes.find(t => String(t.id) === String(voucherTypeId));
  const isCashSettling = selectedType ? CASH_SETTLING_CODES.has(selectedType.code) : false;
  // The settlement (cash/bank) leg is added automatically based on Payment
  // Mode — picking it again as a line's own account would post both sides
  // of the voucher to the same account and silently net to zero.
  const settlementAccountCode = paymentMode === 'Cash' ? '1000' : '1010';
  const lineAccountOptions = isCashSettling ? accounts.filter(a => a.account_code !== settlementAccountCode) : accounts;

  // /institutions is already ordered phase-then-code by the backend, so a
  // single pass groups correctly without re-sorting here.
  const institutionsByPhase = institutions.reduce((groups, i) => {
    const last = groups[groups.length - 1];
    if (last && last.phaseId === i.phase_id) {
      last.items.push(i);
    } else {
      groups.push({ phaseId: i.phase_id, phaseCode: i.phase_code, phaseName: i.phase_name, items: [i] });
    }
    return groups;
  }, []);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        setLoadingRefs(true);
        const [typesRes, ccRes, fundsRes, accountsRes, institutionsRes, vehiclesRes, vendorsRes] = await Promise.all([
          accountsApi.get('/voucher-types'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
          accountsApi.get('/funds'),
          accountsApi.get('/chart-of-accounts'),
          accountsApi.get('/institutions', { params: { is_active: true } }),
          accountsApi.get('/vehicles', { params: { is_active: true } }),
          accountsApi.get('/vendors', { params: { is_active: true } }),
        ]);
        setVoucherTypes(typesRes.data.voucher_types);
        setCostCenters(ccRes.data.cost_centers);
        setFunds(fundsRes.data.funds);
        setAccounts(accountsRes.data.accounts.filter(a => a.is_active));
        setInstitutions(institutionsRes.data.institutions);
        setVehicles(vehiclesRes.data.vehicles);
        setVendors(vendorsRes.data.vendors);
      } catch (err) {
        toast.error('Failed to load reference data');
      } finally {
        setLoadingRefs(false);
      }
    };
    loadRefs();
  }, []);

  // Switch the line shape whenever the voucher type category changes, and
  // start with two blank lines so the form invites a balanced pair.
  useEffect(() => {
    if (!selectedType) { setLines([]); return; }
    setLines(CASH_SETTLING_CODES.has(selectedType.code) ? [emptyCashLine()] : [emptyExplicitLine(), emptyExplicitLine()]);
  }, [voucherTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Early, non-blocking heads-up — the actual hard block against contract_value
  // only happens server-side at Post time (see checkInstitutionContractValue
  // in postingEngine.js), this is just so the field data-entry stage isn't
  // a surprise later.
  useEffect(() => {
    setInstallmentId('');
    if (!institutionId) { setUtilization(null); return; }
    accountsApi.get(`/institutions/${institutionId}/utilization`)
      .then(res => setUtilization(res.data))
      .catch(() => setUtilization(null));
  }, [institutionId]);

  // Vendor Bill options for the chosen vendor — same non-blocking-heads-up
  // pattern as Institution/Installment above; the real remaining-balance
  // enforcement is server-side (checkVendorBill in postingEngine.js).
  useEffect(() => {
    setVendorBillId('');
    if (!vendorId) { setVendorBills([]); return; }
    accountsApi.get('/vendor-bills', { params: { vendor_id: vendorId, status: 'Posted' } })
      .then(res => setVendorBills(res.data.vendor_bills))
      .catch(() => setVendorBills([]));
  }, [vendorId]);

  const updateLine = (index, field, value) => {
    setLines(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const addLine = () => setLines(prev => [...prev, isCashSettling ? emptyCashLine() : emptyExplicitLine()]);
  const removeLine = (index) => setLines(prev => prev.filter((_, i) => i !== index));

  const totals = useMemo(() => {
    if (isCashSettling) return null;
    const debit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const credit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { debit: Math.round(debit * 100) / 100, credit: Math.round(credit * 100) / 100 };
  }, [lines, isCashSettling]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!voucherTypeId) return toast.error('Select a voucher type');
    if (lines.length === 0) return toast.error('Add at least one line');

    const payloadLines = lines.map(l => {
      if (isCashSettling) {
        return {
          account_id: Number(l.account_id),
          description: l.description || undefined,
          gross_amount: Number(l.gross_amount),
          tax_deducted: l.tax_deducted ? Number(l.tax_deducted) : 0,
        };
      }
      return {
        account_id: Number(l.account_id),
        description: l.description || undefined,
        debit: l.debit ? Number(l.debit) : 0,
        credit: l.credit ? Number(l.credit) : 0,
        cost_center_id: l.cost_center_id ? Number(l.cost_center_id) : undefined,
        fund_id: l.fund_id ? Number(l.fund_id) : undefined,
      };
    });

    setSubmitting(true);
    try {
      const response = await accountsApi.post('/vouchers', {
        voucher_type_id: Number(voucherTypeId),
        cost_center_id: costCenterId ? Number(costCenterId) : undefined,
        fund_id: fundId ? Number(fundId) : undefined,
        institution_id: institutionId ? Number(institutionId) : undefined,
        installment_id: installmentId ? Number(installmentId) : undefined,
        vehicle_id: vehicleId ? Number(vehicleId) : undefined,
        vendor_id: vendorId ? Number(vendorId) : undefined,
        vendor_bill_id: vendorBillId ? Number(vendorBillId) : undefined,
        payment_mode: selectedType?.requires_payment_mode ? paymentMode : undefined,
        purpose,
        accounting_date: accountingDate || undefined,
        lines: payloadLines,
      });
      toast.success(`Voucher ${response.data.voucher.voucher_no} created as Draft`);
      navigate(`/vouchers/${response.data.voucher.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create voucher');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingRefs) {
    return <Layout title="New Voucher"><div className="text-center py-8 text-gray-500">Loading...</div></Layout>;
  }

  return (
    <Layout title="New Voucher">
      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-xl shadow p-6 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Voucher Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Voucher Type</label>
              <select required value={voucherTypeId} onChange={(e) => setVoucherTypeId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select type...</option>
                {voucherTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center (default for lines)</label>
              <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">None</option>
                {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fund (default for lines)</label>
              <select value={fundId} onChange={(e) => setFundId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">None</option>
                {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
              </select>
            </div>
            {selectedType?.requires_payment_mode && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
                <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Institution (optional)</label>
              <select value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">None</option>
                {/* Grouped by Phase — the same institute code can legitimately
                    repeat across phases (a fresh contract each procurement
                    cycle), so Phase is what disambiguates which one this is. */}
                {institutionsByPhase.map(group => (
                  <optgroup key={group.phaseId} label={`${group.phaseCode} — ${group.phaseName}`}>
                    {group.items.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {utilization && (
                <p className={`text-xs mt-1 ${utilization.remaining <= 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  Contract Value: {utilization.contract_value.toFixed(2)} | Already Paid: {utilization.already_paid.toFixed(2)} | Remaining: {utilization.remaining.toFixed(2)}
                  {utilization.remaining <= 0 && ' — posting a payment for this institution will be blocked'}
                </p>
              )}
            </div>
            {utilization && utilization.installments.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Installment (optional)</label>
                <select value={installmentId} onChange={(e) => setInstallmentId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">None — not tied to a specific installment</option>
                  {utilization.installments.map(inst => (
                    <option key={inst.id} value={inst.id} disabled={inst.status === 'Paid'}>
                      #{inst.installment_number} — {Number(inst.planned_amount).toFixed(2)}
                      {inst.due_date ? ` — due ${inst.due_date.slice(0, 10)}` : ''} — {inst.status}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle (optional — for Repair/POL costs)</label>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">None</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.code} — {v.registration_number || v.make_model || ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vendor (optional — for bills/payments)</label>
              <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">None</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.code} — {v.name}</option>)}
              </select>
            </div>
            {vendorId && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vendor Bill (optional — paying against a specific bill)</label>
                <select value={vendorBillId} onChange={(e) => setVendorBillId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">None — not tied to a specific bill</option>
                  {vendorBills.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.bill_no ? `#${b.bill_no} — ` : ''}{b.bill_date?.slice(0, 10)} — {Number(b.total_amount).toFixed(2)}
                    </option>
                  ))}
                </select>
                {vendorBillId && (
                  <p className="text-xs mt-1 text-amber-600">
                    Paying against a bill: the line below must use account 2000 — Accounts Payable, not an expense account
                    (the expense was already recognized when the bill posted). Check the vendor's Bills panel for the remaining balance.
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Accounting Date (optional, defaults to today)</label>
              <input type="date" value={accountingDate} onChange={(e) => setAccountingDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
              <input required type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)}
                placeholder="Brief description of this transaction"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        {selectedType && (
          <div className="bg-white rounded-xl shadow p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700">
                Lines {isCashSettling ? '(each line is a debit; settlement + tax are added automatically on posting)' : '(explicit debit/credit — must balance)'}
              </h2>
              <button type="button" onClick={addLine}
                className="flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm font-medium">
                <Plus size={14} /> Add Line
              </button>
            </div>

            {lines.map((line, i) => (
              <div key={i} className="grid gap-2 mb-2 items-start" style={{ gridTemplateColumns: isCashSettling ? '2fr 2fr 1fr 1fr auto' : '2fr 1.5fr 1fr 1fr 1fr 1fr auto' }}>
                <select required value={line.account_id} onChange={(e) => updateLine(i, 'account_id', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Account...</option>
                  {lineAccountOptions.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
                <input type="text" placeholder="Description" value={line.description}
                  onChange={(e) => updateLine(i, 'description', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />

                {isCashSettling ? (
                  <>
                    <input required type="number" step="0.01" min="0" placeholder="Gross amount" value={line.gross_amount}
                      onChange={(e) => updateLine(i, 'gross_amount', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <input type="number" step="0.01" min="0" placeholder="Tax deducted" value={line.tax_deducted}
                      onChange={(e) => updateLine(i, 'tax_deducted', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </>
                ) : (
                  <>
                    <input type="number" step="0.01" min="0" placeholder="Debit" value={line.debit}
                      onChange={(e) => updateLine(i, 'debit', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <input type="number" step="0.01" min="0" placeholder="Credit" value={line.credit}
                      onChange={(e) => updateLine(i, 'credit', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <select value={line.cost_center_id} onChange={(e) => updateLine(i, 'cost_center_id', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">CC (default)</option>
                      {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code}</option>)}
                    </select>
                  </>
                )}

                <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1}
                  className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed p-2">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            {!isCashSettling && totals && (
              <div className={`mt-3 text-sm font-medium flex gap-4 ${totals.debit === totals.credit && totals.debit > 0 ? 'text-green-700' : 'text-red-600'}`}>
                <span>Total Debit: {totals.debit.toFixed(2)}</span>
                <span>Total Credit: {totals.credit.toFixed(2)}</span>
                <span>{totals.debit === totals.credit ? (totals.debit > 0 ? '✓ Balanced' : '') : '✗ Not balanced — must match before submitting'}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/vouchers')}
            className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={submitting || !selectedType}
            className="bg-blue-800 text-white px-6 py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
            {submitting ? 'Creating...' : 'Create as Draft'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default VoucherCreate;
